import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  budgetState,
  EXPENSE_CATEGORIES,
  monthOf,
  monthTotal,
  parseAmount,
  savingsProgress,
  shiftMonth,
  sumByCategory,
  sumByPerson,
} from '@kraftwerk/shared';
import type { ExpenseCategory, RecurringInterval } from '@kraftwerk/shared';
import { currentOwnerId } from '../../auth.js';
import { db } from '../../db/local.js';
import type {
  LocalExpense,
  LocalExpenseCategory,
  LocalHouseholdMember,
  LocalRecurringExpense,
  LocalSavingsDeposit,
  LocalSavingsGoal,
} from '../../db/local.js';
import {
  addDeposit,
  bookDueRecurring,
  deleteExpense,
  deleteRecurring,
  listCategories,
  listDeposits,
  listExpenses,
  listGoals,
  listRecurring,
  saveExpense,
  saveGoal,
  saveRecurring,
  setBudget,
} from '../../db/costs.js';
import { listMembers } from '../../db/shopping.js';
import { useToday } from '../../hooks/useToday.js';
import { formatDay } from '../../format.js';
import { useSwipePager } from '../../gestures/SwipeProvider.js';

export function formatMoney(cents: number, language: string): string {
  return new Intl.NumberFormat(language, { style: 'currency', currency: 'EUR' }).format(
    cents / 100,
  );
}

/** Betrag ohne Währungszeichen fürs Bearbeiten: 1250 → „12,50“. */
function amountText(cents: number, language: string): string {
  return new Intl.NumberFormat(language, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(cents / 100);
}

/**
 * Kosten (E17) im Reiter Haushalt. Ein Monat auf einer Seite: was es
 * gekostet hat, wofür, von wem, gegen welches Budget — darunter das
 * Wiederkehrende und die Sparziele. Kein „wer schuldet wem“.
 */
export function CostsView({ householdId }: { householdId: string | null }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const today = useToday();
  const [month, setMonth] = useState<string | null>(null);
  const shown = month ?? (today ? monthOf(today) : null);

  // Fällige wiederkehrende Kosten buchen, sobald die Seite aufgeht.
  useEffect(() => {
    if (today) void bookDueRecurring(today, db);
  }, [today]);

  const members = useLiveQuery(
    () => listMembers(householdId, db),
    [householdId],
    [] as LocalHouseholdMember[],
  );
  const monthExpenses = useLiveQuery(
    () => (shown ? listExpenses(shown, db) : Promise.resolve([])),
    [shown],
    [] as LocalExpense[],
  );
  const previous = useLiveQuery(
    () => (shown ? listExpenses(shiftMonth(shown, -1), db) : Promise.resolve([])),
    [shown],
    [] as LocalExpense[],
  );
  const categories = useLiveQuery(() => listCategories(db), [], [] as LocalExpenseCategory[]);
  const [editing, setEditing] = useState<LocalExpense | null>(null);
  // Das Formular steht oben, die Ausgabe weit unten: Ohne Sprung sah das
  // Antippen aus, als passiere nichts (Abnahme 23.10.2026).
  const editorRef = useRef<HTMLDivElement>(null);
  const editingId = editing?.id ?? null;
  useEffect(() => {
    const editor = editorRef.current;
    if (editingId && editor && typeof editor.scrollIntoView === 'function') {
      editor.scrollIntoView({ block: 'start' });
    }
  }, [editingId]);

  // Wischen auf der Monatsübersicht blättert Monate (24.10.2026).
  const monthPager = useSwipePager<HTMLDivElement>(
    () => shown && setMonth(shiftMonth(shown, -1)),
    () => shown && setMonth(shiftMonth(shown, 1)),
  );
  if (!shown || !today) return <></>;
  const total = monthTotal(monthExpenses, shown);
  const previousTotal = monthTotal(previous, shiftMonth(shown, -1));
  const nameOf = new Map(members.map((member) => [member.userId, member.displayName]));
  const adults = members.filter((member) => member.role !== 'child');
  const shared = adults.length > 1 ? householdId : null;
  const budgetOf = (key: string) =>
    categories.find((row) => row.key === key && row.householdId === shared)?.budgetCents ?? null;

  return (
    <section aria-labelledby="costs-heading" className="flex flex-col gap-3">
      <h2 id="costs-heading" className="sr-only">
        {t('costs.heading')}
      </h2>

      <div className="kw-hero flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setMonth(shiftMonth(shown, -1))}
            aria-label={t('costs.previousMonth')}
            className="kw-step"
          >
            ←
          </button>
          <p className="kw-kicker text-center">
            {new Date(`${shown}-01T12:00:00`).toLocaleDateString(i18n.language, {
              month: 'long',
              year: 'numeric',
            })}
          </p>
          <button
            type="button"
            onClick={() => setMonth(shiftMonth(shown, 1))}
            aria-label={t('costs.nextMonth')}
            className="kw-step"
          >
            →
          </button>
        </div>
        {/* Nur die Summe gleitet — Pfeile und Monat bleiben stehen. */}
        <div ref={monthPager} className="flex flex-col gap-2">
          <p className="kw-figure text-5xl">{formatMoney(total, i18n.language)}</p>
          <p className="text-xs text-ink-muted">
            {previousTotal > 0
              ? t('costs.vsPrevious', { amount: formatMoney(previousTotal, i18n.language) })
              : t('costs.noPrevious')}
          </p>
        </div>
      </div>

      {editing ? (
        <div ref={editorRef} className="scroll-mt-24">
          <ExpenseForm
            key={editing.id}
            existing={editing}
            members={members}
            householdId={householdId}
            today={today}
            onDone={() => setEditing(null)}
          />
        </div>
      ) : (
        <ExpenseForm
          existing={null}
          members={members}
          householdId={shared}
          today={today}
          onDone={() => undefined}
        />
      )}

      <CategoryBars
        entries={monthExpenses}
        month={shown}
        budgetOf={budgetOf}
        onBudget={(key, cents) => void setBudget(key, cents, shared, db)}
      />

      {members.length > 1 ? (
        <div className="kw-tile flex flex-col gap-2">
          <h3 className="kw-kicker">{t('costs.byPerson')}</h3>
          {sumByPerson(monthExpenses, shown).map((row) => (
            <p key={row.paidBy ?? 'unknown'} className="flex justify-between text-sm">
              <span>
                {row.paidBy
                  ? (nameOf.get(row.paidBy) ?? t('costs.someone'))
                  : t('costs.unknownPayer')}
              </span>
              <span className="font-semibold">{formatMoney(row.cents, i18n.language)}</span>
            </p>
          ))}
          {monthExpenses.length === 0 ? (
            <p className="text-sm text-ink-muted">{t('costs.empty')}</p>
          ) : null}
        </div>
      ) : null}

      <div className="kw-tile flex flex-col gap-2">
        <h3 className="kw-kicker">{t('costs.list')}</h3>
        {monthExpenses.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('costs.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {monthExpenses.map((expense) => (
              <li key={expense.id}>
                <button
                  type="button"
                  onClick={() => setEditing(expense)}
                  className="flex min-h-11 w-full items-center justify-between gap-3 rounded-lg px-1 text-left"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm">
                      {expense.note ?? t(`costs.category.${expense.category}`)}
                    </span>
                    <span className="block text-xs text-ink-muted">
                      {formatDay(expense.date, 'short', i18n.language)} ·{' '}
                      {t(`costs.category.${expense.category}`)}
                      {expense.paidBy && members.length > 1
                        ? ` · ${nameOf.get(expense.paidBy) ?? t('costs.someone')}`
                        : ''}
                      {expense.householdId || members.length < 2 ? '' : ` · ${t('costs.private')}`}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm font-semibold">
                    {formatMoney(expense.amountCents, i18n.language)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <RecurringTile members={members} householdId={shared} today={today} />
      <GoalsTile householdId={shared} today={today} />
    </section>
  );
}

function CategoryPicker({
  value,
  onChange,
}: {
  value: ExpenseCategory;
  onChange: (value: ExpenseCategory) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <fieldset className="flex flex-col gap-1">
      <legend className="kw-label">{t('costs.categoryLabel')}</legend>
      <div className="flex flex-wrap gap-2">
        {EXPENSE_CATEGORIES.map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={value === key}
            onClick={() => onChange(key)}
            className="kw-toggle"
          >
            {t(`costs.category.${key}`)}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function PaidByPicker({
  members,
  value,
  onChange,
}: {
  members: LocalHouseholdMember[];
  value: string | null;
  onChange: (value: string) => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  // Bezahlt haben nur Erwachsene — Kinder sehen die Kosten nicht.
  const adults = members.filter((member) => member.role !== 'child');
  if (adults.length < 2) return null;
  return (
    <fieldset className="flex flex-col gap-1">
      <legend className="kw-label">{t('costs.paidBy')}</legend>
      <div className="flex flex-wrap gap-2">
        {adults.map((member) => (
          <button
            key={member.userId}
            type="button"
            aria-pressed={value === member.userId}
            onClick={() => onChange(member.userId)}
            className="kw-toggle"
          >
            {member.displayName}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Eintragen oder ändern. „Bezahlt von“ ist vorbelegt mit mir und lässt sich
 * ändern — auch nachträglich, wenn jemand anders den Einkauf bezahlt hat.
 */
function ExpenseForm({
  existing,
  members,
  householdId,
  today,
  onDone,
}: {
  existing: LocalExpense | null;
  members: LocalHouseholdMember[];
  householdId: string | null;
  today: string;
  onDone: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const ids = { amount: useId(), date: useId(), note: useId() };
  const me = currentOwnerId();
  const [amount, setAmount] = useState(
    existing ? amountText(existing.amountCents, i18n.language) : '',
  );
  const [category, setCategory] = useState<ExpenseCategory>(
    (existing?.category as ExpenseCategory | undefined) ?? 'food',
  );
  const [date, setDate] = useState(existing?.date ?? today);
  const [note, setNote] = useState(existing?.note ?? '');
  const [paidBy, setPaidBy] = useState<string | null>(existing?.paidBy ?? me);
  const [privateOnly, setPrivateOnly] = useState(existing ? !existing.householdId : false);
  const cents = parseAmount(amount);
  const scope = privateOnly ? null : (existing?.householdId ?? householdId);

  return (
    <form
      className="kw-tile flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (cents === null) return;
        void saveExpense(
          {
            amountCents: cents,
            category,
            categoryId: null,
            date,
            note: note || null,
            paidBy: scope ? paidBy : me,
            householdId: scope,
          },
          existing,
          'manual',
          db,
        ).then(() => {
          setAmount('');
          setNote('');
          onDone();
        });
      }}
    >
      <h3 className="kw-kicker">{existing ? t('costs.edit') : t('costs.add')}</h3>
      <div className="flex gap-2">
        <label htmlFor={ids.amount} className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="kw-label">{t('costs.amount')}</span>
          <input
            id={ids.amount}
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="0,00"
            aria-invalid={amount !== '' && cents === null}
            className="kw-field font-display text-2xl"
          />
        </label>
        <label htmlFor={ids.date} className="flex w-40 flex-col gap-1">
          <span className="kw-label">{t('costs.date')}</span>
          <input
            id={ids.date}
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="kw-field"
          />
        </label>
      </div>
      {amount !== '' && cents === null ? (
        <p role="alert" className="text-xs text-danger">
          {t('costs.invalidAmount')}
        </p>
      ) : null}
      <CategoryPicker value={category} onChange={setCategory} />
      <label htmlFor={ids.note} className="flex flex-col gap-1">
        <span className="kw-label">{t('costs.note')}</span>
        <input
          id={ids.note}
          value={note}
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
          className="kw-field"
        />
      </label>
      {householdId || existing?.householdId ? (
        <button
          type="button"
          aria-pressed={privateOnly}
          onClick={() => setPrivateOnly(!privateOnly)}
          className="kw-toggle self-start"
        >
          {t('costs.privateToggle')}
        </button>
      ) : null}
      {scope ? <PaidByPicker members={members} value={paidBy} onChange={setPaidBy} /> : null}
      <div className="flex gap-2">
        <button type="submit" disabled={cents === null} className="kw-btn kw-btn-small flex-1">
          {existing ? t('costs.save') : t('costs.addSubmit')}
        </button>
        {existing ? (
          <>
            <button
              type="button"
              onClick={() => void deleteExpense(existing, db).then(onDone)}
              className="kw-btn kw-btn-quiet kw-btn-small"
            >
              {t('costs.delete')}
            </button>
            <button type="button" onClick={onDone} className="kw-btn kw-btn-quiet kw-btn-small">
              {t('nutrition.library.cancel')}
            </button>
          </>
        ) : null}
      </div>
    </form>
  );
}

/** Kategorien als Balken — gegen das Budget, wenn eins gesetzt ist. */
function CategoryBars({
  entries,
  month,
  budgetOf,
  onBudget,
}: {
  entries: LocalExpense[];
  month: string;
  budgetOf: (key: string) => number | null;
  onBudget: (key: ExpenseCategory, cents: number | null) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [budgetFor, setBudgetFor] = useState<ExpenseCategory | null>(null);
  const [budgetText, setBudgetText] = useState('');
  const budgetId = useId();
  const sums = sumByCategory(entries, month);
  const withBudget = EXPENSE_CATEGORIES.filter((key) => budgetOf(key) !== null);
  const keys = [
    ...sums.map((row) => row.category as ExpenseCategory),
    ...withBudget.filter((key) => !sums.some((row) => row.category === key)),
  ];
  const max = Math.max(
    1,
    ...sums.map((row) => row.cents),
    ...keys.map((key) => budgetOf(key) ?? 0),
  );

  return (
    <div className="kw-tile flex flex-col gap-3">
      <h3 className="kw-kicker">{t('costs.byCategory')}</h3>
      {keys.length === 0 ? <p className="text-sm text-ink-muted">{t('costs.empty')}</p> : null}
      {keys.map((key) => {
        const spent = sums.find((row) => row.category === key)?.cents ?? 0;
        const budget = budgetOf(key);
        const state = budgetState(spent, budget);
        return (
          <div key={key} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span>{t(`costs.category.${key}`)}</span>
              <span
                className={state === 'over' ? 'text-danger' : state === 'warn' ? 'text-warn' : ''}
              >
                {formatMoney(spent, i18n.language)}
                {budget ? ` / ${formatMoney(budget, i18n.language)}` : ''}
              </span>
            </div>
            <span className="kw-bar kw-bar-thin" aria-hidden="true">
              <i
                style={{
                  width: `${Math.min(100, (spent / max) * 100)}%`,
                  backgroundColor:
                    state === 'over'
                      ? 'var(--color-danger)'
                      : state === 'warn'
                        ? 'var(--color-warn)'
                        : 'var(--color-ink-muted)',
                }}
              />
            </span>
            {state === 'warn' || state === 'over' ? (
              <span className={`text-xs ${state === 'over' ? 'text-danger' : 'text-warn'}`}>
                {t(state === 'over' ? 'costs.budgetOver' : 'costs.budgetWarn')}
              </span>
            ) : null}
          </div>
        );
      })}
      {budgetFor ? (
        <form
          className="flex flex-col gap-2 border-t border-line pt-3"
          onSubmit={(event) => {
            event.preventDefault();
            onBudget(budgetFor, budgetText.trim() === '' ? null : parseAmount(budgetText));
            setBudgetFor(null);
          }}
        >
          <label htmlFor={budgetId} className="kw-label">
            {t('costs.budgetFor', { category: t(`costs.category.${budgetFor}`) })}
          </label>
          <input
            id={budgetId}
            inputMode="decimal"
            value={budgetText}
            onChange={(event) => setBudgetText(event.target.value)}
            placeholder={t('costs.budgetPlaceholder')}
            className="kw-field"
          />
          <div className="flex gap-2">
            <button type="submit" className="kw-btn kw-btn-small flex-1">
              {t('costs.save')}
            </button>
            <button
              type="button"
              onClick={() => setBudgetFor(null)}
              className="kw-btn kw-btn-quiet kw-btn-small flex-1"
            >
              {t('nutrition.library.cancel')}
            </button>
          </div>
        </form>
      ) : (
        <fieldset className="flex flex-col gap-1 border-t border-line pt-3">
          <legend className="text-xs text-ink-muted">{t('costs.setBudget')}</legend>
          <div className="flex flex-wrap gap-2">
            {EXPENSE_CATEGORIES.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  const current = budgetOf(key);
                  setBudgetText(current ? amountText(current, i18n.language) : '');
                  setBudgetFor(key);
                }}
                className="kw-toggle"
              >
                {t(`costs.category.${key}`)}
              </button>
            ))}
          </div>
        </fieldset>
      )}
    </div>
  );
}

function RecurringTile({
  members,
  householdId,
  today,
}: {
  members: LocalHouseholdMember[];
  householdId: string | null;
  today: string;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const ids = { title: useId(), amount: useId(), day: useId() };
  const rules = useLiveQuery(() => listRecurring(db), [], [] as LocalRecurringExpense[]);
  const me = currentOwnerId();
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState<ExpenseCategory>('home');
  const [repeat, setRepeat] = useState<RecurringInterval>('monthly');
  const [day, setDay] = useState('1');
  const [paidBy, setPaidBy] = useState<string | null>(me);
  const cents = parseAmount(amount);
  const dayNumber = Number(day);
  const valid =
    title.trim() !== '' &&
    cents !== null &&
    Number.isInteger(dayNumber) &&
    dayNumber >= 1 &&
    dayNumber <= 31;

  return (
    <div className="kw-tile flex flex-col gap-2">
      <h3 className="kw-kicker">{t('costs.recurring')}</h3>
      {rules.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('costs.recurringEmpty')}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {rules.map((rule) => (
            <li key={rule.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="min-w-0">
                <span className="block truncate">{rule.title}</span>
                <span className="block text-xs text-ink-muted">
                  {t(`costs.interval.${rule.interval}`, { day: rule.day })}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <span className="font-semibold">
                  {formatMoney(rule.amountCents, i18n.language)}
                </span>
                <button
                  type="button"
                  onClick={() => void deleteRecurring(rule, db)}
                  aria-label={t('costs.deleteRecurring', { title: rule.title })}
                  className="kw-btn kw-btn-quiet kw-btn-small px-3"
                >
                  ×
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {adding ? (
        <form
          className="flex flex-col gap-2 border-t border-line pt-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!valid || cents === null) return;
            void saveRecurring(
              {
                title,
                amountCents: cents,
                category,
                interval: repeat,
                day: dayNumber,
                month: repeat === 'yearly' ? Number(today.slice(5, 7)) : null,
                startDate: today,
                paidBy: householdId ? paidBy : me,
                householdId,
              },
              null,
              db,
            ).then(() => {
              setAdding(false);
              setTitle('');
              setAmount('');
              void bookDueRecurring(today, db);
            });
          }}
        >
          <label htmlFor={ids.title} className="flex flex-col gap-1">
            <span className="kw-label">{t('costs.recurringTitle')}</span>
            <input
              id={ids.title}
              value={title}
              maxLength={120}
              onChange={(event) => setTitle(event.target.value)}
              className="kw-field"
            />
          </label>
          <div className="flex gap-2">
            <label htmlFor={ids.amount} className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="kw-label">{t('costs.amount')}</span>
              <input
                id={ids.amount}
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                className="kw-field"
              />
            </label>
            <label htmlFor={ids.day} className="flex w-24 flex-col gap-1">
              <span className="kw-label">{t('costs.day')}</span>
              <input
                id={ids.day}
                inputMode="numeric"
                value={day}
                onChange={(event) => setDay(event.target.value)}
                className="kw-field"
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            {(['monthly', 'yearly'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={repeat === option}
                onClick={() => setRepeat(option)}
                className="kw-toggle"
              >
                {t(`costs.intervalName.${option}`)}
              </button>
            ))}
          </div>
          <CategoryPicker value={category} onChange={setCategory} />
          {householdId ? (
            <PaidByPicker members={members} value={paidBy} onChange={setPaidBy} />
          ) : null}
          <div className="flex gap-2">
            <button type="submit" disabled={!valid} className="kw-btn kw-btn-small flex-1">
              {t('costs.addSubmit')}
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="kw-btn kw-btn-quiet kw-btn-small flex-1"
            >
              {t('nutrition.library.cancel')}
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('costs.addRecurring')}
        </button>
      )}
    </div>
  );
}

function GoalsTile({
  householdId,
  today,
}: {
  householdId: string | null;
  today: string;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const ids = { name: useId(), target: useId() };
  const goals = useLiveQuery(() => listGoals(db), [], [] as LocalSavingsGoal[]);
  const deposits = useLiveQuery(() => listDeposits(db), [], [] as LocalSavingsDeposit[]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [target, setTarget] = useState('');
  const [depositFor, setDepositFor] = useState<string | null>(null);
  const [depositText, setDepositText] = useState('');
  const targetCents = parseAmount(target);
  const depositCents = parseAmount(depositText);

  return (
    <div className="kw-tile flex flex-col gap-3">
      <h3 className="kw-kicker">{t('costs.goals')}</h3>
      {goals.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('costs.goalsEmpty')}</p>
      ) : null}
      {goals.map((goal) => {
        const progress = savingsProgress(
          goal.targetCents,
          deposits.filter((deposit) => deposit.goalId === goal.id),
        );
        return (
          <div key={goal.id} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="font-medium">{goal.name}</span>
              <span className={progress.percent >= 100 ? 'text-good' : ''}>
                {formatMoney(progress.savedCents, i18n.language)} /{' '}
                {formatMoney(goal.targetCents, i18n.language)}
              </span>
            </div>
            <span
              className="kw-bar kw-bar-thin"
              role="img"
              aria-label={t('costs.goalProgress', { percent: progress.percent })}
            >
              <i
                style={{
                  width: `${progress.percent}%`,
                  backgroundColor:
                    progress.percent >= 100 ? 'var(--color-good)' : 'var(--color-accent)',
                }}
              />
            </span>
            {depositFor === goal.id ? (
              <form
                className="flex gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (depositCents === null) return;
                  void addDeposit(goal, depositCents, today, db).then(() => {
                    setDepositFor(null);
                    setDepositText('');
                  });
                }}
              >
                <label className="sr-only" htmlFor={`deposit-${goal.id}`}>
                  {t('costs.depositAmount', { name: goal.name })}
                </label>
                <input
                  id={`deposit-${goal.id}`}
                  inputMode="decimal"
                  value={depositText}
                  onChange={(event) => setDepositText(event.target.value)}
                  placeholder="0,00"
                  className="kw-field min-w-0 flex-1"
                />
                <button
                  type="submit"
                  disabled={depositCents === null}
                  className="kw-btn kw-btn-small shrink-0 px-4"
                >
                  {t('costs.deposit')}
                </button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setDepositFor(goal.id)}
                className="self-start py-1 text-xs text-ink-muted underline underline-offset-2"
              >
                {t('costs.depositOpen')}
              </button>
            )}
          </div>
        );
      })}
      {adding ? (
        <form
          className="flex flex-col gap-2 border-t border-line pt-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!name.trim() || targetCents === null) return;
            void saveGoal({ name, targetCents, householdId }, db).then(() => {
              setAdding(false);
              setName('');
              setTarget('');
            });
          }}
        >
          <label htmlFor={ids.name} className="flex flex-col gap-1">
            <span className="kw-label">{t('costs.goalName')}</span>
            <input
              id={ids.name}
              value={name}
              maxLength={80}
              onChange={(event) => setName(event.target.value)}
              className="kw-field"
            />
          </label>
          <label htmlFor={ids.target} className="flex flex-col gap-1">
            <span className="kw-label">{t('costs.goalTarget')}</span>
            <input
              id={ids.target}
              inputMode="decimal"
              value={target}
              onChange={(event) => setTarget(event.target.value)}
              className="kw-field"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!name.trim() || targetCents === null}
              className="kw-btn kw-btn-small flex-1"
            >
              {t('costs.addSubmit')}
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="kw-btn kw-btn-quiet kw-btn-small flex-1"
            >
              {t('nutrition.library.cancel')}
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('costs.addGoal')}
        </button>
      )}
    </div>
  );
}
